import { createSelector } from 'reselect';

import { MainBoardSearchParams } from '../../domain/depreciation/types';
import { OrderType } from '../../domain/schemas';

type RootState = {
  mainBoardSettings: MainBoardSettingState;
};

/* ---------------------------------------------------------------
 * Action Types
 */

const SEARCH_MAIN_BOARD = 'SEARCH_MAIN_BOARD' as const;
const SEARCH_MAIN_BOARD_FIELD = 'SEARCH_MAIN_BOARD_FIELD' as const;
const SELECT_MAIN_BOARD_COLUMNS_ORDER =
  'SELECT_MAIN_BOARD_COLUMNS_ORDER' as const;
const RESET_MAIN_BOARD_COLUMNS_ORDER =
  'RESET_MAIN_BOARD_COLUMNS_ORDER' as const;
const SELECT_MAIN_BOARD_SORT_ORDER = 'SELECT_MAIN_BOARD_SORT_ORDER' as const;
const SELECT_MAIN_BOARD_PAGINATION = 'SELECT_MAIN_BOARD_PAGINATION' as const;
const SELECT_MAIN_BOARD_IS_TABLE_ANIMATION_ENABLED =
  'SELECT_MAIN_BOARD_IS_TABLE_ANIMATION_ENABLED' as const;
const SELECT_MAIN_BOARD_CURRENT_HALL_NAMES =
  'SELECT_MAIN_BOARD_CURRENT_HALL_NAMES' as const;
const SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER =
  'SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER' as const;
const SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS =
  'SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS' as const;
const SET_MAIN_BOARD_SEARCH_PARAMS = 'SET_MAIN_BOARD_SEARCH_PARAMS' as const;
const RENEW_MAIN_BOARD_SETTING = 'RENEW_MAIN_BOARD_SETTING' as const;

export const MainBoardSettingActionTypes = {
  SEARCH_MAIN_BOARD,
  SEARCH_MAIN_BOARD_FIELD,
  SELECT_MAIN_BOARD_COLUMNS_ORDER,
  RESET_MAIN_BOARD_COLUMNS_ORDER,
  SELECT_MAIN_BOARD_SORT_ORDER,
  SELECT_MAIN_BOARD_PAGINATION,
  SELECT_MAIN_BOARD_IS_TABLE_ANIMATION_ENABLED,
  SELECT_MAIN_BOARD_CURRENT_HALL_NAMES,
  SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
  SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS,
  SET_MAIN_BOARD_SEARCH_PARAMS,
  RENEW_MAIN_BOARD_SETTING,
};

/* ---------------------------------------------------------------
 * Action Creators
 */

/**
 * クリックしたセルの基板番号別データを取得する
 * @param kiCode 機種コード
 */
export function searchMainBoardAction(kiCode: string) {
  return {
    type: SEARCH_MAIN_BOARD,
    payload: { kiCode },
  };
}

/**
 * 指定した表示項目の基板番号別データを再取得する
 * @param fields 表示項目コード一覧
 * @param fieldsOrder 表示項目パターン適用時の列順（任意）。未指定時は従来どおり手動の表示項目変更のみ
 */
export function searchMainBoardFieldAction(
  fields: string[],
  fieldsOrder?: string[]
) {
  return {
    type: SEARCH_MAIN_BOARD_FIELD,
    payload: {
      fields,
      fieldsOrder,
    },
  };
}

/**
 * 基板番号別データテーブルの表示項目の並び順を変更
 */
export function selectMainBoardColumnsOrderAction(
  mainBoardColumnsOrder: string[]
) {
  return {
    type: SELECT_MAIN_BOARD_COLUMNS_ORDER,
    payload: { mainBoardColumnsOrder },
  };
}

/**
 * 基板番号別データテーブルの表示項目の並び順をリセット
 */
export function resetMainBoardColumnsOrderAction() {
  return {
    type: RESET_MAIN_BOARD_COLUMNS_ORDER,
  };
}

/**
 * 基板番号別データのソート順を変更
 */
export function selectMainBoardSortOrderAction(sort: string, order: OrderType) {
  return {
    type: SELECT_MAIN_BOARD_SORT_ORDER,
    payload: { sort, order },
  };
}

/**
 * 基板番号別データのページネーションを変更
 */
export function selectMainBoardPaginationAction(
  page: number,
  rowsPerPage: number
) {
  return {
    type: SELECT_MAIN_BOARD_PAGINATION,
    payload: { page, rowsPerPage },
  };
}

/**
 * 基板番号別データのテーブルアニメーション有効フラグを変更
 */
export function selectMainBoardIsTableAnimationEnabledAction(
  isEnabled: boolean
) {
  return {
    type: SELECT_MAIN_BOARD_IS_TABLE_ANIMATION_ENABLED,
    payload: { isEnabled },
  };
}

/**
 * 基板番号別データの現設置店舗フィルタを変更
 */
export function selectMainBoardCurrentHallNamesAction(hallNames: string[]) {
  return {
    type: SELECT_MAIN_BOARD_CURRENT_HALL_NAMES,
    payload: { hallNames },
  };
}

/**
 * 基板番号別データの特定項目ヘッダーにおけるサブメニュー上のマルチフィルターを変更
 */
export function selectMainBoardTableHeaderSubMenuMultiFilterAction(tableHeaderSubMenuMultiFilter: {
  [columnName: string]: string[];
}) {
  return {
    type: SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER,
    payload: tableHeaderSubMenuMultiFilter,
  };
}

/**
 * 基板番号別データの特定項目ヘッダーにおけるサブメニュー上のマルチフィルターの選択肢を変更
 */
export function selectMainBoardTableHeaderSubMenuMultiFilterSelectOptionsAction(tableHeaderSubMenuMultiFilterSelectOptions: {
  [columnName: string]: string[];
}) {
  return {
    type: SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS,
    payload: tableHeaderSubMenuMultiFilterSelectOptions,
  };
}

/**
 * APIレスポンスのsettingをUI側のsearchParamsに保存する
 * @param fields 表示項目
 * @param sort ソート列
 * @param order ソート順
 */
export function setMainBoardSearchParamsAction(
  fields: string[],
  sort: string,
  order: OrderType
) {
  return {
    type: SET_MAIN_BOARD_SEARCH_PARAMS,
    payload: {
      fields,
      sort,
      order,
    },
  };
}

/**
 * 基板番号別データのUI設定を初期状態にリセット
 */
export function renewMainBoardSettingAction() {
  return {
    type: RENEW_MAIN_BOARD_SETTING,
  };
}

export const MainBoardSettingActionCreators = {
  searchMainBoardAction,
  searchMainBoardFieldAction,
  selectMainBoardColumnsOrderAction,
  resetMainBoardColumnsOrderAction,
  selectMainBoardSortOrderAction,
  selectMainBoardPaginationAction,
  selectMainBoardIsTableAnimationEnabledAction,
  selectMainBoardCurrentHallNamesAction,
  selectMainBoardTableHeaderSubMenuMultiFilterAction,
  selectMainBoardTableHeaderSubMenuMultiFilterSelectOptionsAction,
  setMainBoardSearchParamsAction,
  renewMainBoardSettingAction,
};

/* ---------------------------------------------------------------
 * Actions
 */

export type SearchMainBoardAction = ReturnType<typeof searchMainBoardAction>;
export type SearchMainBoardFieldAction = ReturnType<
  typeof searchMainBoardFieldAction
>;
type SelectMainBoardColumnsOrderAction = ReturnType<
  typeof selectMainBoardColumnsOrderAction
>;
type ResetMainBoardColumnsOrderAction = ReturnType<
  typeof resetMainBoardColumnsOrderAction
>;
type SelectMainBoardSortOrderAction = ReturnType<
  typeof selectMainBoardSortOrderAction
>;
type SelectMainBoardPaginationAction = ReturnType<
  typeof selectMainBoardPaginationAction
>;
type SelectMainBoardIsTableAnimationEnabledAction = ReturnType<
  typeof selectMainBoardIsTableAnimationEnabledAction
>;
type SelectMainBoardCurrentHallNamesAction = ReturnType<
  typeof selectMainBoardCurrentHallNamesAction
>;
type SelectMainBoardTableHeaderSubMenuMultiFilterAction = ReturnType<
  typeof selectMainBoardTableHeaderSubMenuMultiFilterAction
>;
type SelectMainBoardTableHeaderSubMenuMultiFilterSelectOptionsAction =
  ReturnType<
    typeof selectMainBoardTableHeaderSubMenuMultiFilterSelectOptionsAction
  >;
type SetMainBoardSearchParamsAction = ReturnType<
  typeof setMainBoardSearchParamsAction
>;
type RenewMainBoardSettingAction = ReturnType<
  typeof renewMainBoardSettingAction
>;

type MainBoardSettingActions =
  | SearchMainBoardAction
  | SearchMainBoardFieldAction
  | SelectMainBoardColumnsOrderAction
  | ResetMainBoardColumnsOrderAction
  | SelectMainBoardSortOrderAction
  | SelectMainBoardPaginationAction
  | SelectMainBoardIsTableAnimationEnabledAction
  | SelectMainBoardCurrentHallNamesAction
  | SelectMainBoardTableHeaderSubMenuMultiFilterAction
  | SelectMainBoardTableHeaderSubMenuMultiFilterSelectOptionsAction
  | SetMainBoardSearchParamsAction
  | RenewMainBoardSettingAction;

/* ---------------------------------------------------------------
 * State
 */

export type MainBoardSettingState = {
  searchParams: MainBoardSearchParams | undefined;
  /**
   * テーブルのカラムの並び順
   */
  columnsOrder?: string[];
  /**
   * ページネーション設定
   */
  pagination: { page: number; rowsPerPage: number };
  /**
   * テーブル表示アニメーション有効フラグ
   */
  isTableAnimationEnabled: boolean;
  /**
   * 選択されている現設置店舗名のリスト
   */
  selectedCurrentHallNames?: string[];
  /**
   * サブメニュー上のマルチフィルターの選択状態（key: カラム名、value: 選択中のフィルター項目）
   */
  tableHeaderSubMenuMultiFilter?: {
    [columnName: string]: string[];
  };
  /**
   * サブメニュー上のマルチフィルターの選択肢（key: カラム名、value: 選択中のフィルターの選択肢）
   */
  tableHeaderSubMenuMultiFilterSelectOptions?: {
    [columnName: string]: string[];
  };
};

const initialState: MainBoardSettingState = {
  searchParams: undefined,
  columnsOrder: undefined,
  pagination: { page: 0, rowsPerPage: 100 },
  isTableAnimationEnabled: true,
  selectedCurrentHallNames: undefined,
  tableHeaderSubMenuMultiFilter: undefined,
  tableHeaderSubMenuMultiFilterSelectOptions: undefined,
};

/* ---------------------------------------------------------------
 * Selector
 */

/**
 * [基板番号別データ] settingの全ての状態を取得する
 */
export function mainBoardSettingSelector(rootState: RootState) {
  return rootState.mainBoardSettings;
}

/**
 * [基板番号別データ] 検索条件を取得する
 */
export const mainBoardSearchParamsSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSetting) => mainBoardSetting.searchParams
);

/**
 * [基板番号別データ] 表示項目の列順を取得する
 */
export const mainBoardColumnsOrderSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.columnsOrder
);

/**
 * [基板番号別データ] ソート列を取得する
 */
export const mainBoardSortSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.searchParams?.sort
);

/**
 * [基板番号別データ] ソート順を取得する
 */
export const mainBoardOrderSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.searchParams?.order
);

/**
 * [基板番号別データ] ページネーション設定を取得する
 */
export const mainBoardPaginationSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.pagination
);

/**
 * [基板番号別データ] テーブルアニメーション有効フラグを取得する
 */
export const mainBoardIsTableAnimationEnabledSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.isTableAnimationEnabled
);

/**
 * [基板番号別データ] 選択されている現設置店舗名のリストを取得する
 */
export const mainBoardCurrentHallNamesSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.selectedCurrentHallNames ?? []
);

/**
 * [基板番号別データ] 特定項目ヘッダーにおけるサブメニュー上のマルチフィルターで指定されている値を返す
 */
export const mainBoardTableHeaderSubMenuMultiFilterSelector = createSelector(
  mainBoardSettingSelector,
  (mainBoardSettings) => mainBoardSettings.tableHeaderSubMenuMultiFilter
);

/**
 * [基板番号別データ] 特定項目ヘッダーにおけるサブメニュー上のマルチフィルターの選択肢を返す
 */
export const mainBoardTableHeaderSubMenuMultiFilterSelectOptionsSelector =
  createSelector(
    mainBoardSettingSelector,
    (mainBoardSettings) =>
      mainBoardSettings.tableHeaderSubMenuMultiFilterSelectOptions
  );

/* ---------------------------------------------------------------
 * Reducer
 */

export function mainBoardSettingReducer(
  state: MainBoardSettingState = initialState,
  action: MainBoardSettingActions
): MainBoardSettingState {
  switch (action.type) {
    case SEARCH_MAIN_BOARD_FIELD:
      return {
        ...state,
        searchParams: {
          ...(state.searchParams ?? {}),
          fields: action.payload.fields,
        },
      };
    case SELECT_MAIN_BOARD_COLUMNS_ORDER:
      return {
        ...state,
        columnsOrder: action.payload.mainBoardColumnsOrder,
      };
    case RESET_MAIN_BOARD_COLUMNS_ORDER:
      return {
        ...state,
        columnsOrder: undefined,
      };
    case SELECT_MAIN_BOARD_SORT_ORDER:
      return {
        ...state,
        searchParams: {
          ...(state.searchParams ?? {}),
          sort: action.payload.sort,
          order: action.payload.order,
        },
      };
    case SELECT_MAIN_BOARD_PAGINATION:
      return {
        ...state,
        pagination: {
          page: action.payload.page,
          rowsPerPage: action.payload.rowsPerPage,
        },
      };
    case SELECT_MAIN_BOARD_IS_TABLE_ANIMATION_ENABLED:
      return {
        ...state,
        isTableAnimationEnabled: action.payload.isEnabled,
      };
    case SELECT_MAIN_BOARD_CURRENT_HALL_NAMES:
      return {
        ...state,
        selectedCurrentHallNames: action.payload.hallNames,
      };
    case SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER:
      return {
        ...state,
        tableHeaderSubMenuMultiFilter: {
          ...state.tableHeaderSubMenuMultiFilter,
          ...action.payload,
        },
      };
    case SELECT_MAIN_BOARD_TABLE_HEADER_SUB_MENU_MULTI_FILTER_SELECT_OPTIONS:
      return {
        ...state,
        tableHeaderSubMenuMultiFilterSelectOptions: {
          ...state.tableHeaderSubMenuMultiFilterSelectOptions,
          ...action.payload,
        },
      };
    case SET_MAIN_BOARD_SEARCH_PARAMS: {
      const { fields, sort, order } = action.payload;

      return {
        ...state,
        searchParams: {
          ...(state.searchParams ?? {}),
          fields,
          sort,
          order,
        },
      };
    }
    case RENEW_MAIN_BOARD_SETTING:
      return initialState;
    default:
      return state;
  }
}
